import { api } from '@/api';
import router from '@/router';
import { getLocalToken, removeLocalToken, saveLocalToken } from '@/utils';
import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import {
    commitSetCanAddElements,
    commitSetFlows, commitSetLandingElements,
} from './mutations';
import { FlowsState } from './state';
import { dispatchCheckApiError } from '../main/actions';

type MainContext = ActionContext<FlowsState, State>;

export const actions = {
    async actionGetFlows(context: MainContext) {
        try {
            const response = await api.getFlows(context.rootState.main.token);
            if (response.data) {
                commitSetFlows(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAddFlow(context: MainContext, payload: { data: any }) {
        try {
            const response = await api.addFlow(payload.data, context.rootState.main.token);
            await dispatchGetFlows(context);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateFlow(context: MainContext, payload: { id: number, data: any }) {
        try {
            const response = await api.updateFlow(payload.id, payload.data, context.rootState.main.token);
            await dispatchGetFlows(context);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteFlow(context: MainContext, payload: { id: number }) {
        try {
            const response = await api.deleteFlow(payload.id, context.rootState.main.token);
            await dispatchGetFlows(context);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetLandingElements(context: MainContext, payload: { id: string }) {
        try {
            const response = await api.getLandingElements(context.rootState.main.token, payload.id);
            if (response.data) {
                commitSetLandingElements(context, response.data.elements);
                commitSetCanAddElements(context, response.data.can_add_elements);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionDeleteLandingElement(context: MainContext, payload: { landingId: string, elementId: number }) {
        try {
            const response = await api.deleteLandingElement(
                payload.landingId, payload.elementId, context.rootState.main.token,
            );
            await dispatchGetLandingElements(context, {id: payload.landingId});
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateLandingElement(context: MainContext,
                                     payload: { landingId: string, elementId: number, data: any }) {
        try {
            const response = await api.updateLandingElement(
                payload.landingId, payload.elementId, payload.data, context.rootState.main.token,
            );
            await dispatchGetLandingElements(context, {id: payload.landingId});
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAddLandingElement(context: MainContext, payload: { landingId: string, data: any }) {
        try {
            const response = await api.addLandingElement(
                payload.landingId, payload.data, context.rootState.main.token,
            );
            await dispatchGetLandingElements(context, {id: payload.landingId});
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCheckLandingUniqueName(context: MainContext, payload: { data: any }) {
        try {
            const response = await api.checkLandingUniqueName(
                context.rootState.main.token, payload.data,
            );
            return response.data;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return {
                matches: true,
                error: 'Произошла ошибка',
            };
        }
    },
};

const { dispatch } = getStoreAccessors<FlowsState | any, State>('');

export const dispatchGetFlows = dispatch(actions.actionGetFlows);
export const dispatchAddFlows = dispatch(actions.actionAddFlow);
export const dispatchUpdateFlows = dispatch(actions.actionUpdateFlow);
export const dispatchDeleteFlow = dispatch(actions.actionDeleteFlow);
export const dispatchGetLandingElements = dispatch(actions.actionGetLandingElements);
export const dispatchDeleteLandingElement = dispatch(actions.actionDeleteLandingElement);
export const dispatchUpdateLandingElement = dispatch(actions.actionUpdateLandingElement);
export const dispatchAddLandingElement = dispatch(actions.actionAddLandingElement);
export const dispatchCheckLandingUniqueName = dispatch(actions.actionCheckLandingUniqueName);
